// src/pages/ClinicalDataCollector/components/output/sections/AsthmaSection.jsx

import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import AirIcon from '@mui/icons-material/Air';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const AsthmaSection = ({ data }) => {
  if (!data) return null;

  const {
    code,
    description,
    severity,
    controlScores = {},
    spirometry = [],
    maintenanceTherapy = [],
    rescueTherapy = [],
    triggers = [],
    managementPlans = []
  } = data;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Section: Adult Asthma"
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<AirIcon fontSize="large" color="primary" />}
      />
      <Divider />
      <CardContent>
        {/* ICD-10 Code */}
        <Typography
          variant="subtitle1"
          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
        >
          <LocalHospitalIcon sx={{ mr: 1 }} />
          Code: {code} — {description}
        </Typography>

        {/* Severity */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <ShowChartIcon sx={{ mr: 1 }} /> Severity
        </Typography>
        <Typography variant="body2" paragraph>
          {severity}
        </Typography>

        {/* Control Scores */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <CheckCircleIcon sx={{ mr: 1 }} /> Control Scores
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Test</TableCell>
              <TableCell>Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell>ACT</TableCell>
              <TableCell>{controlScores.ACT}</TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>ACQ</TableCell>
              <TableCell>{controlScores.ACQ}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Spirometry */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <ShowChartIcon sx={{ mr: 1 }} /> Spirometry
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Measure</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {spirometry.map((row, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{row.measure}</TableCell>
                <TableCell>{row.value}</TableCell>
                <TableCell>{row.date}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Maintenance Therapy */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <LocalPharmacyIcon sx={{ mr: 1 }} /> Maintenance Therapy
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {maintenanceTherapy.map((tx, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <LocalPharmacyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={tx} />
            </ListItem>
          ))}
        </List>

        {/* Rescue Therapy */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <LocalPharmacyIcon sx={{ mr: 1 }} /> Rescue Therapy
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {rescueTherapy.map((tx, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <LocalPharmacyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={tx} />
            </ListItem>
          ))}
        </List>

        {/* Triggers */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <AirIcon sx={{ mr: 1 }} /> Triggers
        </Typography>
        <List dense sx={{ mb: 3 }}>
          {triggers.map((trigger, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <AirIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={trigger} />
            </ListItem>
          ))}
        </List>

        {/* Management Plans */}
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
        >
          <CheckCircleIcon sx={{ mr: 1 }} /> Management Plan
        </Typography>
        <List dense>
          {managementPlans.map((plan, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={plan} />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default AsthmaSection;
